/**
 * Users API Client
 *
 * Endpoints for managing organization members and their roles.
 * All requests go through the centralized API SDK for consistent
 * authentication, token refresh, and 401 handling.
 *
 * Error Handling:
 * All methods throw ApiClientError on failure. Use try/catch blocks
 * or .catch() to handle errors. The ApiClientError provides:
 * - Structured error information (code, type, message)
 * - Validation violations for form errors
 * - Type-checking methods (isValidationError(), isNotFound(), etc.)
 */

import { api } from './sdk';
import { buildListQuery, type Paginated, type PaginationParams } from './pagination';

// Re-export error handling utilities for consumers
export { ApiClientError, isApiClientError } from './sdk';

export interface OrganizationMember {
  id: string;
  auth0Id: string;
  email: string;
  name?: string;
  picture?: string;
  createdAt: string;
  roles: string[];
  primaryRole: string;
}

export interface MemberDetail {
  id: string;
  auth0Id: string;
  email: string;
  name?: string;
  picture?: string;
  createdAt: string;
  roles: { id: string; name: string }[];
  primaryRole: string;
}

export interface AvailableRole {
  name: string;
  description: string;
}

export interface UpdateRoleResponse {
  userId: string;
  email: string;
  newRole: string;
  removedRoles: number;
}

/**
 * Get members in the current user's organization (paginated).
 * @throws {ApiClientError} On API failure
 */
export const getOrganizationMembers = (
  params: PaginationParams = {},
): Promise<Paginated<OrganizationMember>> =>
  api.get<Paginated<OrganizationMember>>(
    `/api/organizations/members${buildListQuery(params)}`,
  );

/**
 * Get a specific member's details
 * @throws {ApiClientError} On API failure (404 if member not found)
 */
export const getMemberDetail = (userId: string): Promise<MemberDetail> =>
  api.get<MemberDetail>(`/api/organizations/members/${encodeURIComponent(userId)}`);

/**
 * Update a member's role
 * @throws {ApiClientError} On API failure (404 if member not found, 403 if insufficient permissions, 400 if validation fails)
 */
export const updateMemberRole = (userId: string, role: string): Promise<UpdateRoleResponse> =>
  api.put<UpdateRoleResponse>(`/api/organizations/members/${encodeURIComponent(userId)}/role`, { role });

/**
 * Get list of roles that can be assigned by organization owners
 * @throws {ApiClientError} On API failure (403 if not an owner)
 */
export const getAvailableRoles = (): Promise<AvailableRole[]> =>
  api.get<AvailableRole[]>('/api/organizations/available-roles');

export interface WelcomeState {
  show: boolean;
  invitesRemaining: number;
}

/**
 * Welcome-modal state for the current user, read from /api/auth/me.
 * @throws {ApiClientError} On API failure
 */
export const getWelcomeState = (): Promise<WelcomeState> =>
  api.get<{ welcome: WelcomeState }>(`/api/auth/me`).then((r) => r.welcome);

/**
 * Permanently dismiss the welcome modal for the current user. Idempotent.
 * @throws {ApiClientError} On API failure
 */
export const dismissWelcome = (): Promise<{ dismissed: boolean }> =>
  api.post<{ dismissed: boolean }>(`/api/auth/welcome/dismiss`);
